/**
 * Native Support Desk — Admin console (super_admin / partner_admin)
 *
 * Two views:
 *   - List: queue with status filter
 *   - Detail: full thread + reply + transition + assign
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { ArrowLeft, ChatCircle, Envelope, CheckCircle, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = ["open", "pending", "resolved", "closed"];

export default function SupportDeskAdmin() {
  return (
    <AppShell title="Customer Support">
      <div className="max-w-6xl mx-auto" data-testid="support-admin-page">
        <h1 className="text-2xl font-semibold text-zinc-900 mb-2">Customer Support Desk</h1>
        <p className="text-sm text-zinc-600 mb-6">Tickets from external customers via the support portal or email.</p>
        <SupportQueue />
      </div>
    </AppShell>
  );
}

function SupportQueue() {
  const [data, setData] = useState({ items: [], counts: {}, loading: true });
  const [status, setStatus] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/support/admin/tickets", { params: status ? { status } : {} });
      setData({ ...r.data, loading: false });
    } catch (e) { toast.error(formatApiError(e)); setData((d) => ({ ...d, loading: false })); }
  };
  useEffect(() => { load(); }, [status]);

  if (data.loading) return <Card className="p-6 text-sm text-zinc-500">Loading…</Card>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setStatus("")} className={`px-3 py-1.5 rounded-full text-xs ${!status ? "bg-zinc-900 text-white" : "bg-zinc-100"}`} data-testid="filter-all">All ({Object.values(data.counts).reduce((a, b) => a + b, 0)})</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-full text-xs capitalize ${status === s ? "bg-zinc-900 text-white" : "bg-zinc-100"}`} data-testid={`filter-${s}`}>
            {s} ({data.counts[s] || 0})
          </button>
        ))}
      </div>

      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-zinc-500 italic py-6">No tickets in this view.</TableCell></TableRow>
            ) : data.items.map((t) => (
              <TableRow key={t.id} className="cursor-pointer hover:bg-zinc-50" onClick={() => navigate(`/app/admin/support/${t.id}`)} data-testid={`ticket-row-${t.id}`}>
                <TableCell className="font-mono text-xs">{t.number}</TableCell>
                <TableCell className="font-medium">{t.subject}</TableCell>
                <TableCell className="text-xs text-zinc-500">
                  {t.customer_name}<br/>{t.customer_email}
                </TableCell>
                <TableCell>
                  <Badge variant={t.status === "open" ? "default" : t.status === "resolved" ? "secondary" : "outline"}>{t.status}</Badge>
                </TableCell>
                <TableCell>
                  {t.channel === "email" ? <Envelope size={14} className="text-zinc-500" /> : <ChatCircle size={14} className="text-zinc-500" />}
                </TableCell>
                <TableCell className="text-xs text-zinc-500">{(t.updated_at || "").slice(0, 16).replace("T", " ")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* Detail page                                                               */
/* ──────────────────────────────────────────────────────────────────────── */
export function SupportTicketDetail() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true });
  const [staff, setStaff] = useState([]);
  const [reply, setReply] = useState("");
  const [internalOnly, setInternalOnly] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [t, s] = await Promise.all([
        api.get(`/support/admin/tickets/${tid}`),
        api.get("/support/admin/staff"),
      ]);
      setState({ loading: false, ...t.data });
      setStaff(s.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); setState({ loading: false, error: true }); }
  };
  useEffect(() => { load(); }, [tid]);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/support/admin/tickets/${tid}/reply`, {
        body: reply, internal_only: internalOnly,
      });
      setReply(""); setInternalOnly(false);
      toast.success("Reply sent");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const transition = async (status) => {
    try {
      await api.post(`/support/admin/tickets/${tid}/transition`, { status });
      toast.success(`Marked ${status}`); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const assign = async (uid) => {
    try {
      await api.post(`/support/admin/tickets/${tid}/assign`, { assignee_user_id: uid === "__none__" ? null : uid });
      toast.success("Assignee updated"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (state.loading) return <AppShell title="Loading…"><Card className="p-6">Loading…</Card></AppShell>;
  if (state.error) return <AppShell title="Error"><Card className="p-6 text-red-600">Couldn&apos;t load ticket.</Card></AppShell>;
  const { ticket, messages } = state;
  const isFinal = ticket.status === "resolved" || ticket.status === "closed";

  return (
    <AppShell title={`Ticket ${ticket.number}`}>
      <div className="max-w-4xl mx-auto" data-testid="ticket-detail-page">
        <button onClick={() => navigate("/app/admin/support")} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-3" data-testid="back-to-queue"><ArrowLeft size={14} /> Back to queue</button>

        {/* Header */}
        <Card className="p-6 mb-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs text-zinc-500 font-mono">{ticket.number}</div>
              <h1 className="text-xl font-semibold text-zinc-900 mt-1">{ticket.subject}</h1>
              <div className="text-xs text-zinc-500 mt-1">
                {ticket.customer_name} &lt;{ticket.customer_email}&gt; · opened {(ticket.created_at || "").slice(0, 16).replace("T", " ")}
              </div>
            </div>
            <Badge variant={ticket.status === "open" ? "default" : ticket.status === "resolved" ? "secondary" : "outline"}>{ticket.status}</Badge>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Select value={ticket.assignee_user_id || "__none__"} onValueChange={assign}>
              <SelectTrigger className="w-[200px]" data-testid="assignee-select"><SelectValue placeholder="Assign to…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Unassigned —</SelectItem>
                {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-2">
              {ticket.status !== "pending" && <Button size="sm" variant="outline" onClick={() => transition("pending")} data-testid="mark-pending">Pending</Button>}
              {ticket.status !== "resolved" && <Button size="sm" onClick={() => transition("resolved")} data-testid="mark-resolved"><CheckCircle size={14} className="mr-1" />Resolve</Button>}
              {ticket.status === "resolved" && <Button size="sm" variant="outline" onClick={() => transition("closed")} data-testid="mark-closed">Close</Button>}
              {(ticket.status === "resolved" || ticket.status === "closed") && <Button size="sm" variant="outline" onClick={() => transition("open")} data-testid="mark-open">Reopen</Button>}
            </div>
          </div>
        </Card>

        {/* Thread */}
        <Card className="p-6">
          <div className="space-y-4">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`p-3 rounded-lg ${
                  m.internal_only ? "bg-amber-50 border border-amber-200"
                  : m.author_type === "customer" ? "bg-zinc-100"
                  : m.author_type === "staff" ? "bg-indigo-50"
                  : "bg-zinc-50 italic text-xs"
                }`}
                data-testid={`msg-${m.id}`}
              >
                <div className="text-xs text-zinc-500 mb-1 flex items-center gap-2">
                  <span className="font-semibold text-zinc-700">{m.author_name}</span>
                  <span>{m.author_type === "customer" ? "customer" : m.author_type === "staff" ? "staff" : "system"}</span>
                  {m.internal_only && <Badge variant="outline" className="bg-amber-100 border-amber-300 text-amber-900">internal</Badge>}
                  {m.channel === "email" && <span className="text-zinc-400">via email</span>}
                  <span className="ml-auto">{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="whitespace-pre-wrap text-sm text-zinc-800">{m.body}</div>
              </div>
            ))}
          </div>

          {/* Reply */}
          <div className="mt-6 border-t border-zinc-200 pt-4">
            <Textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" data-testid="admin-reply-input" />
            <div className="mt-2 flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-zinc-600">
                <Switch checked={internalOnly} onCheckedChange={setInternalOnly} data-testid="internal-toggle" />
                Internal note (not sent to customer)
              </label>
              <Button onClick={send} disabled={sending || !reply.trim()} data-testid="admin-send-reply">{sending ? "Sending…" : "Send reply"}</Button>
            </div>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
